import { useState, useEffect } from 'react';
import { IUser } from '../../../interfaces/user';
import { Avatar, Button, IconButton, Typography } from '@mui/material';
import { GridColDef, GridAlignment } from '@mui/x-data-grid';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { MAX_PAGE_LIMIT } from '../../../constants/numbers';
import IResponse, { IPagination } from '../../../interfaces/response';
import BaranDataGrid from '../../common/BaranDataGrid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { stringToColor } from '../../../utils/stringToColor';
import { LoadingButton } from '@mui/lab';
import BasicModal from '../../common/BasicModal';
import { exportToExcel } from '../../../utils/file';
import FilterTableList from '../../common/FilterTableList';

export default function UserList() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [fetchedData, setFetchedData] = useState<IUser[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(true);
	const [deleteLoading, setDeleteLoading] = useState(false);
	// const [searchTerm, setSearchTerm] = useState('');
	const navigate = useNavigate();
	// const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [deleteUser, setDeleteUser] = useState<IUser>();
	const [refresh, setRefresh] = useState(1);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleCreateNewUser = () => {
		navigate('/admin/user/new');
	};

	const handleDelete = (value: IUser) => () => {
		setDeleteUser(value);
	};

	const handleExportExcel = () => {
		exportToExcel({ data: fetchedData, fileName: 'payment_list', t });
	};

	const columns: GridColDef<IUser>[] = [
		{
			field: 'name',
			headerName: t('first_name_and_last_name'),
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			flex: 2,
			renderCell: params => {
				let image = params.row.image;
				let imageUrl;
				if (image instanceof File) {
					imageUrl = URL.createObjectURL(image);
				} else if (typeof image === 'string') {
					imageUrl = image;
				}
				return (
					<div className="flex h-full flex-row items-center gap-4">
						<Avatar sx={{ bgcolor: stringToColor(params.row.name) }} src={imageUrl ?? ''}>
							{params.row.name.substring(0, 1)}
						</Avatar>
						<Typography>{params.row.name}</Typography>
					</div>
				);
			},
		},
		{
			field: 'phone',
			headerName: t('phone'),
			width: 150,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('date'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton component={Link} to={`/admin/users/${params.row.id}/edit`}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton component={Link} to={`/admin/users/${params.row.id}`}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IUser[]>>({
			url: `/admin/users/`,
			params: {
				page: page,
				limit: pageSize,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, refresh]);

	// const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = e => {
	// 	setSearchTerm(e.target.value);
	// };

	const handleCloseDeleteDialog = () => {
		setDeleteUser(undefined);
	};

	const handleDeleteUser = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: `/admin/users/${deleteUser?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteUser(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
			});
	};

	return (
		<div className={'w-full'}>
			<FilterTableList setFetchedData={setFetchedData} />

			<BaranDataGrid<IUser> loading={loading} isCreateBtn2={true} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} onClick={handleCreateNewUser} onClick2={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('add_new_user')} actionButtonLable2={t('export_excel')} />

			<BasicModal
				open={!!deleteUser}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteUser} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_user_description', { user: deleteUser?.name })}</Typography>
			</BasicModal>
		</div>
	);
}
